import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLoginMutation, useGetHomepageSettingsQuery } from "../redux/services/api";
import { loginSuccess } from "../redux/slices/authSlice";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import { setSettings } from "../redux/slices/homepageSlice";
import { selectSettings, selectHomepageLoading } from "../redux/slices/homepageSlice";

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [error, setError] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Redux state
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const settings = useSelector(selectSettings);
    const settingsLoading = useSelector(selectHomepageLoading);
    
    // RTK Query hooks
    const [login, { isLoading: loading }] = useLoginMutation();
    const { data: settingsData } = useGetHomepageSettingsQuery();

    // Load settings into Redux when fetched
    useEffect(() => {
        if (settingsData && settingsData.success) {
            dispatch(setSettings(settingsData.settings));
        }
    }, [settingsData, dispatch]);

    const from = location.state?.from?.pathname || "/dashboard";

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, from]);

    const handleInputChange = (field, value) => {
        if (error) {
            setError(""); // Clear error when user starts typing
        }

        if (field === "email") {
            setEmail(value);
        } else if (field === "password") {
            setPassword(value);
        } else if (field === "remember") {
            setRemember(value);
        }
    };

    const handleSubmit = async () => {
        setError("");

        try {
            const result = await login({ email, password, remember }).unwrap();

            if (result.success && result.user) {
                // Update Redux state
                dispatch(loginSuccess({
                    user: result.user,
                    token: result.token,
                }));
                navigate(from, { replace: true });
            } else {
                setError(result.message || "Login failed");
            }
        } catch (err) {
            console.error("Login error:", err);
            setError(err.data?.message || "An unexpected error occurred. Please try again.");
        }
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-form-wrapper">
                    <div className="login-header">
                        <div className="login-logo">
                            {!settingsLoading && settings.branding.logo ? (
                                <img
                                    src={settings.branding.logo}
                                    alt={
                                        settings.branding.companyName ||
                                        "Company Logo"
                                    }
                                    className="login-logo-image"
                                />
                            ) : (
                                <div className="login-logo-placeholder">
                                    {!settingsLoading &&
                                    settings.branding.companyName
                                        ? settings.branding.companyName
                                              .charAt(0)
                                              .toUpperCase()
                                        : "S"}
                                </div>
                            )}
                        </div>
                        <h2 className="login-title">Admin Login</h2>
                        <p className="login-subtitle">
                            Sign in to access the dashboard
                        </p>
                    </div>

                    <div className="login-form">
                        {error && <div className="login-error">{error}</div>}

                        <div className="form-group">
                            <label htmlFor="email" className="form-label">
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) =>
                                    handleInputChange("email", e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        handleSubmit();
                                    }
                                }}
                                className="form-input"
                                placeholder="Enter your email"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) =>
                                    handleInputChange(
                                        "password",
                                        e.target.value
                                    )
                                }
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        handleSubmit();
                                    }
                                }}
                                className="form-input"
                                placeholder="Enter your password"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={remember}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "remember",
                                            e.target.checked
                                        )
                                    }
                                    disabled={loading}
                                />
                                <span className="checkbox-text">
                                    Remember me
                                </span>
                            </label>
                        </div>

                        <button
                            type="button"
                            className="login-button"
                            disabled={loading}
                            onClick={handleSubmit}
                        >
                            {loading ? "Signing in..." : "Sign In"}
                        </button>
                    </div>

                    <div className="login-footer">
                        <p className="login-footer-text">
                            Welcome to Silver Fort Admin Panel
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
